// TODO: Use filestack-js npm library (with lazy load)

import window from '@atlas/tool/box/browser/window';
import constants from '@atlas/tool/box/constants';
import loadScript from '@atlas/tool/box/network/loadScript';

export default async function filestack(config: TFileStackConfig = {}): Promise<TFileStackClient> {
    if (clientPromise) return clientPromise;

    // TODO: remove this config and use defaults after all customers have updated their Content Security Policy
    const { useAtlasCname = true, scriptSrc, apiKey = constants.FILE_PICKER_API_KEY } = config;

    const domain = useAtlasCname ? 'fs.atlas.so' : 'filestackapi.com';
    const finalScriptSrc = scriptSrc || `//static.${domain}/filestack-js/4.x.x/filestack.min.js`;
    const fileStackOptions = useAtlasCname ? { cname: 'fs.atlas.so' } : {};

    await loadScript(finalScriptSrc);

    if (!window || (window && !window.filestack)) throw new Error('FileStack is not initialized');

    clientPromise = Promise.resolve(window.filestack!.init(apiKey, fileStackOptions));
    return clientPromise;
}

// have to turn off the default social sources – not working with a custom CNAME
export const basePickerOptions = { fromSources: ['local_file_system', 'url'] };

// #region Helpers

let clientPromise: Promise<TFileStackClient> | null = null;

// #endregion

// #region Types

type TFileStackConfig = {
    useAtlasCname?: boolean;
    scriptSrc?: string;
    apiKey?: string;
};

export type TFileStackUploadedFile = {
    container: string;
    filename: string;
    handle: string;
    key: string;
    size: number;
    url: string;
};

export interface TFileStackPickerResult {
    filesFailed: unknown[];
    filesUploaded: TFileStackUploadedFile[];
}

type TFileStackSelectedFile = {
    filename: string;
    handle: undefined;
    mimetype: string;
    name?: string;
    originalFile: { name: string; type: string; size: number };
    originalPath: string;
    size: number;
    source: string;
    uploadId: string;
    url: undefined;
};

export type TFileStackPickerOptions = {
    maxFiles?: number;
    accept?: string | string[];
    container?: string | HTMLElement | null;
    transformations?: {
        crop?: boolean | { aspectRatio?: number; force?: boolean };
        circle?: boolean;
    };
    fromSources?: string[];
    onUploadDone?: (result: TFileStackPickerResult) => void;
    onCancel?: () => void;
    onFileUploadFailed?: (file: TFileStackSelectedFile) => void;
    onOpen?: () => void; // TODO: Add argument
    onFileSelected?: (file: TFileStackSelectedFile) => void;
};

export interface TFileStackPicker {
    open(): void;
}

export type TFileStackProgressEvent = { totalPercent: number; totalBytes: number };

export interface TFileStackClient {
    picker(options: TFileStackPickerOptions): TFileStackPicker;
    upload(
        file: File,
        options: { altText?: string; onProgress?: (e: TFileStackProgressEvent) => void },
    ): Promise<TFileStackUploadedFile>;
    multiupload(files: File[], options: { altText?: string }): Promise<TFileStackUploadedFile[]>;
    storeURL(url: string): Promise<TFileStackUploadedFile>;
}

export interface TFileStack {
    init(apiKey: string, options?: { cname?: string }): TFileStackClient;
}

declare global {
    interface Window {
        filestack?: TFileStack;
    }
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, test, expect, vi, beforeEach, afterEach } = import.meta.vitest;
    describe('filestack', () => {
        const instanceMock = {};

        beforeEach(() => {
            if (!window) return;
            const fileStackMock = { init: vi.fn() };
            vi.stubGlobal('filestack', fileStackMock);
            if (!window.filestack) throw new Error('Could not setup filestack in test environment');
            vi.spyOn(window.filestack, 'init');
            fileStackMock.init.mockReturnValueOnce(instanceMock);

            vi.mock('@atlas/tool/box/network/loadScript', () => {
                return { default: vi.fn().mockResolvedValue(undefined) };
            });
        });

        afterEach(() => {
            vi.resetAllMocks();
            vi.unstubAllGlobals();
            clientPromise = null;
        });

        test('executes filestack in global scope and returns its value: with Atlas CNAME', async () => {
            if (!window) return;
            expect(window.filestack?.init).toBeCalledTimes(0);
            expect(await filestack()).toBe(instanceMock);
            expect(window.filestack?.init).toBeCalledTimes(1);
            expect(window.filestack?.init).toBeCalledWith(constants.FILE_PICKER_API_KEY, { cname: 'fs.atlas.so' });
        });
        // temporary, to be removed eventually
        test('executes filestack in global scope and returns its value: w/o Atlas CNAME', async () => {
            if (!window) return;
            expect(window.filestack?.init).toBeCalledTimes(0);
            expect(await filestack({ useAtlasCname: false })).toBe(instanceMock);
            expect(window.filestack?.init).toBeCalledTimes(1);
            expect(window.filestack?.init).toBeCalledWith(constants.FILE_PICKER_API_KEY, {});
        });
    });
}

// #endregion
